/**
 * Hooks for AFIP Casillero Electrónico (WSCCOMU)
 *
 * Uses Auth0 token directly (like useConsultarCuitAFIP) — NOT Supabase client.
 * AFIP endpoints go through Netlify Functions, not Supabase.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { casilleroService, CasilleroError } from '@/services/casilleroService'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import type {
  FiltrosCasillero,
  ComunicacionDetalle,
  ComunicacionResumen,
} from '@/types/afip'

export interface CasilleroResumen {
  noLeidas: number
  ultima: ComunicacionResumen | null
}

export const casilleroKeys = {
  all: ['casillero'] as const,
  lists: () => [...casilleroKeys.all, 'list'] as const,
  list: (cuit: string, filtros?: FiltrosCasillero) =>
    [...casilleroKeys.lists(), cuit, filtros] as const,
  detail: (cuit: string, id: number) =>
    [...casilleroKeys.all, 'detail', cuit, id] as const,
}

/**
 * Si una consulta al Casillero que falló se vuelve a intentar.
 *
 * Un pedido que la función rechaza por lo que pide —un rango de fechas más largo del que ARCA
 * acepta, un permiso que falta— vuelve igual por más que se repita, y mientras tanto la pantalla
 * sigue cargando. Se reintenta una vez lo que puede cambiar solo: un fallo de red o del servidor.
 */
export const reintentarConsulta = (fallos: number, error: Error): boolean => {
  if (error instanceof CasilleroError && error.status >= 400 && error.status < 500) {
    return false
  }
  return fallos < 1
}

export function useComunicaciones(
  cuit: string | null,
  filtros?: FiltrosCasillero
) {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: casilleroKeys.list(cuit ?? '', filtros),
    queryFn: async () => {
      const token = await getAccessTokenSilently()
      return casilleroService.listarComunicaciones(token, cuit!, { ...filtros, todas: true })
    },
    enabled: !!cuit,
    staleTime: 60_000,
    retry: reintentarConsulta,
  })
}

/**
 * Resumen liviano del Casillero para mostrar en la ficha del cliente:
 * solo trae el conteo de "no leídas" y los datos del último mensaje.
 *
 * - Cache key incluye `organizacionId` para evitar mix entre orgs.
 * - Gateado por `puede.verCasilleroCliente` y `cuit` válido (11 dígitos).
 * - staleTime 5min para no hammerear AFIP por cada apertura del modal.
 */
export function useCasilleroResumen(cuit: string | null) {
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  const cuitLimpio = cuit ? cuit.replace(/\D/g, '') : ''

  return useQuery<CasilleroResumen>({
    queryKey: [
      ...casilleroKeys.list(cuitLimpio, { estado: 'NO_LEIDA', resultadosPorPagina: 1 }),
      organizacionId,
    ],
    queryFn: async () => {
      const token = await getAccessTokenSilently()
      const r = await casilleroService.listarComunicaciones(token, cuitLimpio, {
        estado: 'NO_LEIDA',
        resultadosPorPagina: 1,
      })
      return {
        noLeidas: r.totalRegistros,
        ultima: r.comunicaciones[0] ?? null,
      }
    },
    enabled:
      cuitLimpio.length === 11 &&
      !!puede.verCasilleroCliente &&
      !!organizacionId,
    staleTime: 5 * 60_000,
    retry: reintentarConsulta,
  })
}

export function useConsumirComunicacion() {
  const { getAccessTokenSilently } = useAuth0()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cuit,
      comunicacionId,
    }: {
      cuit: string
      comunicacionId: number
    }): Promise<ComunicacionDetalle> => {
      // RG (AFIP) 4280/2018: consumir una comunicación equivale a notificarse
      // legalmente. Solo perfiles con `abrirComunicacionARCA` pueden ejecutarlo.
      assertPermiso(puede, 'abrirComunicacionARCA')
      const token = await getAccessTokenSilently()
      return casilleroService.consumirComunicacion(
        token,
        cuit,
        comunicacionId
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: casilleroKeys.lists() })
    },
    onError: (error: Error) => {
      console.error('Error consumiendo comunicación:', error)
      toast.error(`Error al abrir comunicación: ${error.message}`)
    },
  })
}
